import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useSelection } from "../SelectionContext";
import { useAuth } from "../context/AuthContext";
import apiClient from "../api/apiClient";
import SecureLogger from "../utils/secureLogger.js";
import API_CONFIG from "../config/api.js";
import { getAuthorizedCompanyContext } from "../utils/companyContext.js";

const API_URL = API_CONFIG.BASE_URL;
apiClient.defaults.baseURL = API_URL;

const { Title } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const PAYMENT_MODE_TYPES = {
  cash: { value: "cash", color: "green", label: "Especes" },
  bank: { value: "bank", color: "blue", label: "Virement bancaire" },
  card: { value: "card", color: "purple", label: "Carte de credit" },
  cheque: { value: "cheque", color: "orange", label: "Cheque" },
  mobile: { value: "mobile", color: "cyan", label: "Mobile Money" },
  other: { value: "other", color: "default", label: "Autre" },
};

const isSuperAdminUser = (user) =>
  Boolean(user?.is_superadmin || user?.role === "SysAdmin");

const PaymentModeModal = ({
  visible,
  onCancel,
  paymentMode,
  onSubmit,
  companies = [],
  currentCompanyId = null,
  currentCompanyName = "",
  isSuperAdmin = false,
}) => {
  const [form] = Form.useForm();
  const { user, hasPermission } = useAuth();
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [localCompanies, setLocalCompanies] = useState([]);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const resolvedCompanyName =
    currentCompanyName ||
    user?.company?.name ||
    (currentCompanyId ? `Entreprise #${currentCompanyId}` : "");

  useEffect(() => {
    if (companies && companies.length > 0) {
      setLocalCompanies(companies);
      return;
    }

    if (!isSuperAdmin) {
      setLocalCompanies(
        currentCompanyId
          ? [{ id: currentCompanyId, name: resolvedCompanyName }]
          : []
      );
      return;
    }

    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const response = await apiClient.get("/api/companies/for-modals");
        setLocalCompanies(response.data.companies || []);
      } catch (error) {
        SecureLogger.error("Failed to load companies for payment modes");
        notification.error({
          message: "Erreur",
          description: "Impossible de charger la liste des entreprises.",
        });
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [companies, currentCompanyId, isSuperAdmin, resolvedCompanyName]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    form.resetFields();

    if (paymentMode) {
      form.setFieldsValue({
        name: paymentMode.name,
        mode_type: paymentMode.mode_type || "bank",
        company_id: paymentMode.company_id || undefined,
        credentials: paymentMode.credentials || undefined,
      });
      return;
    }

    if (currentCompanyId) {
      form.setFieldsValue({
        company_id: currentCompanyId,
      });
    }
  }, [currentCompanyId, form, paymentMode, visible]);

  return (
    <Modal
      title={
        paymentMode?.id
          ? "Modifier un Mode de Paiement"
          : "Ajouter un Mode de Paiement"
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={isSmallScreen ? "95%" : 520}
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          name="name"
          label="Nom du Mode de Paiement"
          rules={[
            {
              required: true,
              message: "Veuillez saisir le nom du mode de paiement",
            },
          ]}
        >
          <Input placeholder="Ex: Especes, Carte bancaire, etc." />
        </Form.Item>

        <Form.Item
          name="mode_type"
          label="Type"
          rules={[{ required: true, message: "Veuillez selectionner un type" }]}
        >
          <Select placeholder="Selectionner un type">
            {Object.values(PAYMENT_MODE_TYPES).map((type) => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {isSuperAdmin ? (
          <Form.Item
            name="company_id"
            label="Entreprise (optionnel)"
            tooltip="Laissez vide pour un mode de paiement disponible a toutes les entreprises"
          >
            <Select
              placeholder="Selectionner une entreprise"
              allowClear
              loading={loadingCompanies}
              showSearch
              filterOption={(input, option) =>
                String(option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {localCompanies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        ) : (
          <>
            <Form.Item name="company_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item label="Entreprise">
              <Input
                value={resolvedCompanyName || "Entreprise non definie"}
                disabled
              />
            </Form.Item>
          </>
        )}

        <Form.Item name="credentials" label="Informations (optionnel)">
          <Input.TextArea
            placeholder="Informations supplementaires: coordonnees bancaires, identifiants, etc."
            rows={3}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            style={{ marginRight: 8 }}
            disabled={
              paymentMode?.id
                ? !hasPermission("Admin.ModesPaiement.edit")
                : !hasPermission("Admin.ModesPaiement.create")
            }
          >
            {paymentMode?.id ? "Mettre a jour" : "Creer"}
          </Button>
          <Button onClick={onCancel}>Annuler</Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const PaymentModeList = () => {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [paymentModes, setPaymentModes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState(null);
  const [filterByCompany, setFilterByCompany] = useState(true);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const isSuperAdmin = isSuperAdminUser(user);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const contextCompanyId =
    typeof selectedCompany === "object" && selectedCompany
      ? selectedCompany.id
      : typeof selectedCompany === "number"
      ? selectedCompany
      : null;

  const authorizedContextCompanyId = getAuthorizedCompanyContext();
  const companyId = isSuperAdmin
    ? contextCompanyId
    : authorizedContextCompanyId || Number(user?.company_id) || null;

  const shouldFilterByCompany = isSuperAdmin
    ? filterByCompany
    : Boolean(companyId);
  const canToggleCompanyFilter = isSuperAdmin && Boolean(companyId);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!isSuperAdmin) {
        const assignedCompany = Array.isArray(user?.assigned_warehouses)
          ? user.assigned_warehouses.find(
              (warehouse) =>
                Number(warehouse?.company_id) === Number(companyId)
            )
          : null;
        const companyName =
          assignedCompany?.company_name ||
          assignedCompany?.company?.name ||
          (Number(user?.company?.id) === Number(companyId)
            ? user?.company?.name
            : null) ||
          `Entreprise #${companyId}`;

        setCompanies(
          companyId
            ? [
                {
                  id: companyId,
                  name: companyName,
                },
              ]
            : []
        );
        return;
      }

      try {
        const response = await apiClient.get("/api/companies/for-modals");
        setCompanies(response.data.companies || []);
      } catch (error) {
        SecureLogger.error("Failed to load companies list");
        notification.error({
          message: "Erreur",
          description: "Impossible de charger la liste des entreprises.",
        });
      }
    };

    fetchCompanies();
  }, [
    companyId,
    isSuperAdmin,
    user?.assigned_warehouses,
    user?.company?.id,
    user?.company?.name,
  ]);

  const getCompanyName = (id) => {
    if (!id) {
      return "Generale";
    }

    if (Number(user?.company?.id) === Number(id) && user?.company?.name) {
      return user.company.name;
    }

    const company = companies.find((item) => Number(item.id) === Number(id));
    return company ? company.name : `Entreprise #${id}`;
  };

  const companyName = getCompanyName(companyId);
  const pageSize = 10;

  const fetchPaymentModes = async (page = 1) => {
    setLoading(true);
    try {
      let url = `/api/payment-modes?page=${page}&limit=${pageSize}&search=${searchTerm}`;

      if (companyId && shouldFilterByCompany) {
        url += `&company_id=${companyId}`;
        SecureLogger.debug(`Filtering payment modes for company: ${companyName}`);
      }

      SecureLogger.debug(
        `API call for payment modes: ${url.replace(
          /company_id=\d+/,
          "company_id=***"
        )}`
      );

      const response = await apiClient.get(url);
      setPaymentModes(response.data.paymentModes);
      setTotalItems(response.data.pagination.totalItems);
      setCurrentPage(page);
    } catch (error) {
      SecureLogger.error("Failed to load payment modes");
      notification.error({
        message: "Erreur",
        description: "Erreur lors du chargement des modes de paiement.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentModes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    fetchPaymentModes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, shouldFilterByCompany]);

  const handleCreateOrUpdate = async (values) => {
    setLoading(true);
    try {
      const payload = { ...values };

      if (!isSuperAdmin && companyId) {
        payload.company_id = companyId;
      } else if (companyId && shouldFilterByCompany && !payload.company_id) {
        payload.company_id = companyId;
      }

      if (selectedPaymentMode?.id) {
        await apiClient.put(
          `/api/payment-modes/${selectedPaymentMode.id}`,
          payload
        );
        notification.success({
          message: "Succes",
          description: "Mode de paiement mis a jour avec succes.",
        });
      } else {
        await apiClient.post("/api/payment-modes", payload);
        notification.success({
          message: "Succes",
          description: "Mode de paiement cree avec succes.",
        });
      }

      setModalVisible(false);
      setSelectedPaymentMode(null);
      fetchPaymentModes(currentPage);
    } catch (error) {
      SecureLogger.error("Failed to save payment mode");
      notification.error({
        message: "Erreur",
        description:
          error.response?.data?.error ||
          "Erreur lors de l'enregistrement du mode de paiement.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (record) => {
    confirm({
      title: "Etes-vous sur de vouloir supprimer ce mode de paiement ?",
      icon: <ExclamationCircleOutlined />,
      okText: "Oui",
      cancelText: "Non",
      onOk: async () => {
        setLoading(true);
        try {
          await apiClient.delete(`/api/payment-modes/${record.id}`, {
            params: { company_id: companyId },
          });
          notification.success({
            message: "Succes",
            description: "Mode de paiement supprime avec succes.",
          });

          if (paymentModes.length === 1 && currentPage > 1) {
            fetchPaymentModes(currentPage - 1);
          } else {
            fetchPaymentModes(currentPage);
          }
        } catch (error) {
          SecureLogger.error("Failed to delete payment mode");
          notification.error({
            message: "Erreur",
            description:
              error.response?.data?.error ||
              "Erreur lors de la suppression du mode de paiement.",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Type",
      dataIndex: "mode_type",
      key: "mode_type",
      render: (modeType) => {
        const typeConfig =
          PAYMENT_MODE_TYPES[modeType] || PAYMENT_MODE_TYPES.other;
        return <Tag color={typeConfig.color}>{typeConfig.label}</Tag>;
      },
    },
    {
      title: "Entreprise",
      dataIndex: "company_name",
      key: "company_name",
      render: (recordCompanyName, record) => {
        if (record.company_id) {
          return (
            <Tag color="blue">
              {recordCompanyName || getCompanyName(record.company_id)}
            </Tag>
          );
        }

        return <Tag color="default">General</Tag>;
      },
      responsive: ["sm"],
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip
            title={
              !hasPermission("Admin.ModesPaiement.edit")
                ? "Permissions insuffisantes"
                : "Modifier"
            }
          >
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setSelectedPaymentMode(record);
                setModalVisible(true);
              }}
              disabled={!hasPermission("Admin.ModesPaiement.edit")}
            />
          </Tooltip>
          <Tooltip
            title={
              !hasPermission("Admin.ModesPaiement.delete")
                ? "Permissions insuffisantes"
                : "Supprimer"
            }
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
              disabled={!hasPermission("Admin.ModesPaiement.delete")}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="payment-mode-list" style={{ marginTop: 20 }}>
      <Card
        title={
          <Title level={isSmallScreen ? 5 : 4} style={{ marginBottom: 0 }}>
            Gestion des Modes de Paiement
          </Title>
        }
        style={{ marginBottom: 20 }}
      >
        <div className="mb-5">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} lg="auto" className="mb-3 lg:mb-0">
              <Tooltip
                title={
                  !hasPermission("Admin.ModesPaiement.create")
                    ? "Permissions insuffisantes"
                    : "Ajouter un Mode de Paiement"
                }
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedPaymentMode(null);
                    setModalVisible(true);
                  }}
                  disabled={!hasPermission("Admin.ModesPaiement.create")}
                  className="w-full lg:w-auto"
                >
                  Ajouter un Mode de Paiement
                </Button>
              </Tooltip>
            </Col>

            <Col xs={24} lg={{ flex: "auto" }} className="mb-3 lg:mb-0">
              <Input
                placeholder="Rechercher..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full"
                allowClear
              />
            </Col>

            {canToggleCompanyFilter ? (
              <Col xs={24} lg="auto" className="mb-3 lg:mb-0">
                <Tooltip
                  title={
                    filterByCompany
                      ? "Desactiver le filtre par entreprise"
                      : "Activer le filtre par entreprise"
                  }
                >
                  <Button
                    type={filterByCompany ? "primary" : "default"}
                    icon={<FilterOutlined />}
                    onClick={() => setFilterByCompany(!filterByCompany)}
                    className="w-full lg:w-auto"
                  >
                    Filtre par Entreprise
                  </Button>
                </Tooltip>
              </Col>
            ) : null}
          </Row>
        </div>

        {companyId && shouldFilterByCompany ? (
          <Alert
            message={`Modes de paiement filtres pour l'entreprise: ${companyName}`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              canToggleCompanyFilter ? (
                <Button size="small" onClick={() => setFilterByCompany(false)}>
                  Voir tous
                </Button>
              ) : null
            }
          />
        ) : null}

        <Spin spinning={loading}>
          <Table
            dataSource={paymentModes}
            columns={columns}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize,
              total: totalItems,
              showSizeChanger: false,
              showTotal: (total) => `Total: ${total} modes de paiement`,
              onChange: (page) => fetchPaymentModes(page),
            }}
            scroll={{ x: "max-content" }}
            locale={{
              emptyText: (
                <div style={{ padding: "20px 0" }}>
                  {companyId && shouldFilterByCompany ? (
                    <div>
                      <p>Aucun mode de paiement trouve pour cette entreprise.</p>
                      {canToggleCompanyFilter ? (
                        <Button onClick={() => setFilterByCompany(false)}>
                          Voir tous les modes de paiement
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    "Aucun mode de paiement trouve."
                  )}
                </div>
              ),
            }}
          />
        </Spin>

        <PaymentModeModal
          visible={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setSelectedPaymentMode(null);
          }}
          paymentMode={selectedPaymentMode}
          onSubmit={handleCreateOrUpdate}
          companies={companies}
          currentCompanyId={companyId}
          currentCompanyName={companyName}
          isSuperAdmin={isSuperAdmin}
        />
      </Card>
    </div>
  );
};

const GestionModePaiement = () => {
  return <PaymentModeList />;
};

export default GestionModePaiement;
